import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@eluve/components';
import { DynamicArtifactTemplate } from '@eluve/llm-outputs';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { templateBuilderStore } from '@eluve/smart-blocks';

import { createOutputTemplateMutation } from './operations';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';

export const useCreateTemplate = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const isOrganizationRoute = useIsOrganizationRoute();
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();

  const [createOutputTemplate] = useMutation(createOutputTemplateMutation, {
    onCompleted: ({ insertLlmOutputTemplatesOne: template }) => {
      toast.success('Template created');
      templateBuilderStore.send({ type: 'reset' });
      if (template) {
        navigate(`../${template.id}`);
      }
    },
    onError: () => toast.error('Failed to create template'),
  });

  const handleSubmit = async (isDraft: boolean) => {
    const { context } = templateBuilderStore.getSnapshot();
    const dynamicArtifact: DynamicArtifactTemplate = {
      name: context.name,
      description: context.description,
      blocks: context.blocks,
    };
    await createOutputTemplate({
      variables: {
        input: {
          name: context.name,
          description: context.description,
          llmOutputType: context.outputType,
          ...(isOrganizationRoute ? { tenantId } : {}),
          variants: {
            data: [
              {
                template: dynamicArtifact,
                variantNotes: context.variantNotes,
                isCurrentDefault: !isDraft,
                isDraft: isDraft,
              },
            ],
          },
          // Grant access to creator if they're submitting an organization template
          ...(isOrganizationRoute && !isDraft
            ? {
                users_llm_output_templates: {
                  data: [{ userId: userId!, tenantId: tenantId! }],
                },
              }
            : {}),
        },
      },
    });
  };

  return {
    handleSaveDraftTemplate: () => handleSubmit(true),
    handleSubmitTemplate: () => handleSubmit(false),
  };
};
